import {useTranslation} from "react-i18next";
import {InsightProps, InsightText} from "../Insight/Insight";
import {
  transportMethod,
  TransportMethod,
  StatisticsGroupWrapper,
  StatisticsType,
  TransportCategory
} from "@ots-frontend/ots-data";
import {useMemo} from "react";
import {ActionIcon, Flex, Group, MantineColor, Stack} from "@mantine/core";
import {IconChartDots3, IconSearch, IconTruck} from "@tabler/icons-react";
import {InsightContainer, InsightIconType} from "../InsightContainer/InsightContainer";

export type StatisticInsightProps = Pick<InsightProps, 'loading'> & {
  title?: string
  statistics?: StatisticsGroupWrapper | StatisticsGroupWrapper[],
  type: StatisticsType,
  method?: TransportMethod
  rateModels?: string[]
  category?: TransportCategory
  color?: ((value: number | undefined) => MantineColor | undefined) | MantineColor
  onSearchClick?: () => void
}

export const StatisticInsight = (props: StatisticInsightProps) => {
  const {
    title,
    statistics,
    type,
    method,
    rateModels,
    category,
    color,
    onSearchClick,
    ...rest
  } = props

  const {t} = useTranslation()

  const _title = useMemo(() => {
    return title ?? t(type.displayValue.id, type.displayValue.defaultMessage)
  }, [t, type, title])

  const value = useMemo(() => {
    let value: number | undefined = undefined

    if (statistics) {
      let wrappers: StatisticsGroupWrapper[] = []
      wrappers = wrappers.concat(statistics)

      for (const wrapper of wrappers) {
        if (type.cost && rateModels && rateModels.length > 0) {
          for (const rateModel of rateModels) {
            if (typeof value === 'undefined') {
              value = 0
            }
            value += wrapper.getValue(method ?? type.method, type, rateModel, category)
          }
        } else {
          if (typeof value === 'undefined') {
            value = 0
          }
          value += wrapper.getValue(method ?? type.method, type, undefined, category)
        }
      }
    }

    return value
  }, [category, method, rateModels, statistics, type])

  const icon: InsightIconType | undefined = useMemo(() => {
    return type.unitType === 'currency' ? 'coin' : undefined
  }, [type])

  const insightColor: MantineColor | undefined = useMemo(() => {
    if (color) {
      if (typeof color === 'function') {
        return color(value)
      } else {
        return color
      }
    }
    return undefined
  }, [value, color])

  return (
    <InsightContainer {...rest} title={_title} icon={icon}>
      <Stack>
        <Group align="flex-end" spacing="xs" mt={25}>
          <InsightText
            type={type}
            value={value}
            color={insightColor}/>
        </Group>
        <Group position="apart" align="flex-end" spacing="xs">
          <Flex justify="flex-end">
            {(!method || method === transportMethod.trucking) &&
              <IconTruck size="1.125rem"/>
            }
            {(!method || method === transportMethod.pipeline) &&
              <IconChartDots3 size="1.125rem"/>
            }
          </Flex>
          <Flex justify="flex-end">
            {onSearchClick &&
              <ActionIcon onClick={() => onSearchClick()}>
                <IconSearch size="1.125rem"/>
              </ActionIcon>
            }
          </Flex>
        </Group>
      </Stack>
    </InsightContainer>
  )
}
